import React, { useState, useRef, useEffect } from 'react';
import { calculationsAPI } from '../services/api';
import { PRESET_ORBITS, ENGINE_TYPES } from '../utils/constants';
import ResultsSection from './ResultsSection';

const CalculatorSection = () => {
  const [initialOrbit, setInitialOrbit] = useState({ altitude: 400, eccentricity: 0, inclination: 0 });
  const [targetOrbit, setTargetOrbit] = useState({ altitude: 35786, eccentricity: 0, inclination: 0 });
  const [selectedInitialPreset, setSelectedInitialPreset] = useState(null);
  const [selectedTargetPreset, setSelectedTargetPreset] = useState(null);
  const [userModifiedPreset, setUserModifiedPreset] = useState(false);
  const [spacecraftMass, setSpacecraftMass] = useState(1000);
  const [engineType, setEngineType] = useState('chemical');
  const [transferType, setTransferType] = useState('auto');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Реф для автоматического скролла
  const resultsRef = useRef(null);

  // Следим за появлением результатов и скроллим к ним
  useEffect(() => {
    if (results && resultsRef.current) {
      resultsRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [results]);

  const handlePresetChange = (orbitKey, presetKey, preset) => {
    if (orbitKey === 'initial') {
      setInitialOrbit(preset);
      setSelectedInitialPreset(presetKey);
      setUserModifiedPreset(false);
    } else {
      setTargetOrbit(preset);
      setSelectedTargetPreset(presetKey);
      setUserModifiedPreset(false);
    }
  };

  const handleManualChange = (orbitKey, field, value) => {
    const numValue = Number(value);
    if (orbitKey === 'initial') {
      setInitialOrbit({ ...initialOrbit, [field]: numValue });
      if (selectedInitialPreset) setUserModifiedPreset(true);
    } else {
      setTargetOrbit({ ...targetOrbit, [field]: numValue });
      if (selectedTargetPreset) setUserModifiedPreset(true);
    }
  };

  const handleCalculate = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await calculationsAPI.calculateTransfer({
        initial_orbit: initialOrbit,
        target_orbit: targetOrbit,
        spacecraft_mass: spacecraftMass,
        engine_type: engineType,
        transfer_type: transferType,
      });
      
      // Как только стейт обновится, сработает useEffect выше
      setResults(response);
    } catch (err) {
      setError(err.response?.data?.detail || 'Calculation failed. Please check inputs.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="calculator" className="py-16 px-6 bg-dark-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-extrabold text-white mb-4 tracking-tight uppercase">
            Orbital Transfer Calculator
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Professional tool for calculating Hohmann and Bi-elliptic transfers between earth orbits.
          </p>
        </div>

        {error && (
          <div className="mb-8 p-5 bg-red-900/20 border-2 border-red-500/50 rounded-2xl text-red-200 flex items-center gap-4 animate-pulse">
            <span className="text-2xl">⚠️</span> {error}
          </div>
        )}

        <div className="space-y-8">
          {/* ORBIT SELECTION ROW */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Initial Orbit Card */}
            <div className="bg-dark-800 p-8 rounded-3xl border border-gray-700 shadow-2xl">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <h3 className="text-2xl font-bold text-primary-400 uppercase tracking-tight">Initial Orbit</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(PRESET_ORBITS).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => handlePresetChange('initial', key, preset)}
                      className={`px-4 py-2 text-xs font-bold rounded-xl border-2 transition-all uppercase tracking-tighter ${
                        selectedInitialPreset === key && !userModifiedPreset
                          ? 'bg-primary-600 border-primary-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]'
                          : 'bg-dark-700 border-gray-600 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[
                  { label: 'Altitude (km)', key: 'altitude' },
                  { label: 'Eccentricity', key: 'eccentricity' },
                  { label: 'Inclination (°)', key: 'inclination' }
                ].map((field) => (
                  <div key={field.key}>
                    <label className="block text-xs font-black text-gray-500 mb-2 uppercase tracking-widest">
                      {field.label}
                    </label>
                    <input
                      type="number"
                      value={initialOrbit[field.key]}
                      step={field.key === 'eccentricity' ? '0.001' : '1'}
                      onChange={(e) => handleManualChange('initial', field.key, e.target.value)}
                      className="w-full px-5 py-4 bg-dark-900 border-2 border-gray-700 rounded-2xl text-white text-xl font-mono focus:border-primary-500 outline-none transition-all"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Target Orbit Card */}
            <div className="bg-dark-800 p-8 rounded-3xl border border-gray-700 shadow-2xl">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <h3 className="text-2xl font-bold text-rose-400 uppercase tracking-tight">Target Orbit</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(PRESET_ORBITS).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => handlePresetChange('target', key, preset)}
                      className={`px-4 py-2 text-xs font-bold rounded-xl border-2 transition-all uppercase tracking-tighter ${
                        selectedTargetPreset === key && !userModifiedPreset
                          ? 'bg-rose-600 border-rose-500 text-white shadow-[0_0_15px_rgba(225,29,72,0.4)]'
                          : 'bg-dark-700 border-gray-600 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[
                  { label: 'Altitude (km)', key: 'altitude' },
                  { label: 'Eccentricity', key: 'eccentricity' },
                  { label: 'Inclination (°)', key: 'inclination' }
                ].map((field) => (
                  <div key={field.key}>
                    <label className="block text-xs font-black text-gray-500 mb-2 uppercase tracking-widest">
                      {field.label}
                    </label>
                    <input
                      type="number"
                      value={targetOrbit[field.key]}
                      step={field.key === 'eccentricity' ? '0.001' : '1'}
                      onChange={(e) => handleManualChange('target', field.key, e.target.value)}
                      className="w-full px-5 py-4 bg-dark-900 border-2 border-gray-700 rounded-2xl text-white text-xl font-mono focus:border-rose-500 outline-none transition-all"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SPACECRAFT & EXECUTION ROW */}
          <div className="bg-dark-800 p-8 rounded-3xl border border-gray-700 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-end">
              <div>
                <label className="block text-xs font-black text-gray-500 mb-3 uppercase tracking-widest">Dry Mass (kg)</label>
                <input
                  type="number"
                  value={spacecraftMass}
                  onChange={(e) => setSpacecraftMass(Number(e.target.value))}
                  className="w-full px-6 py-4 bg-dark-900 border-2 border-gray-700 rounded-2xl text-white text-xl font-mono outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-black text-gray-500 mb-3 uppercase tracking-widest">Engine Type</label>
                <select
                  value={engineType}
                  onChange={(e) => setEngineType(e.target.value)}
                  className="w-full px-6 py-4 bg-dark-900 border-2 border-gray-700 rounded-2xl text-white text-lg outline-none focus:border-primary-500 cursor-pointer"
                >
                  {Object.entries(ENGINE_TYPES).map(([key, engine]) => (
                    <option key={key} value={key}>{engine.name} ({engine.isp}s)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-black text-gray-500 mb-3 uppercase tracking-widest">Transfer Strategy</label>
                <select
                  value={transferType}
                  onChange={(e) => setTransferType(e.target.value)}
                  className="w-full px-6 py-4 bg-dark-900 border-2 border-gray-700 rounded-2xl text-white text-lg outline-none focus:border-primary-500 cursor-pointer"
                >
                  <option value="auto">Auto-optimize</option>
                  <option value="hohmann">Hohmann</option>
                  <option value="bielliptic">Bi-elliptic</option>
                </select>
              </div>
              <button
                onClick={handleCalculate}
                disabled={loading}
                className="w-full h-[64px] bg-primary-600 hover:bg-primary-500 disabled:bg-gray-700 text-white rounded-2xl font-black text-xl uppercase tracking-widest transition-all shadow-[0_10px_20px_rgba(37,99,235,0.3)] active:scale-95 flex items-center justify-center overflow-hidden"
              >
                {loading ? (
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Processing</span>
                  </div>
                ) : (
                  "Calculate"
                )}
              </button>
            </div>
          </div>
        </div>

        {/* RESULTS SECTION WITH SCROLL ANCHOR */}
        {results && (
          <div ref={resultsRef} className="mt-16 pt-12 border-t-2 border-gray-800 animate-in fade-in slide-in-from-bottom-8 duration-700">
            <ResultsSection 
              results={results} 
              initialOrbit={initialOrbit} 
              targetOrbit={targetOrbit} 
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default CalculatorSection;